import React, { memo } from 'react';
import { Book, Brain, Calendar, Gamepad2, Lightbulb, Heart, ArrowRight } from 'lucide-react';
import { CTAButton } from './CTAButton';
export function Tomos() {
  const tomos = [{
    icon: <Brain className="w-8 h-8 text-teal-600" data-id="element-277" />,
    title: 'TOMO 1: Hábitos de Blindaje Mental',
    subtitle: 'El terreno que hace que todo lo demás funcione',
    desc: 'Antes de entrenar la memoria hay que crear el terreno adecuado. Este tomo establece los hábitos básicos diarios que preparan al cerebro para funcionar mejor.',
    bullets: ['El semáforo del día para arrancar con atención consciente', 'Hidratación activa combinada con ejercicios de memoria', 'Orientación temporal para mantenerse anclado al presente', 'Planificación diaria para reducir la carga mental que causa olvidos', 'Sistema de seguimiento del sueño y la energía', 'Disparadores de recuerdos profundos para trabajar la memoria a largo plazo']
  }, {
    icon: <Calendar className="w-8 h-8 text-teal-600" data-id="element-278" />,
    title: 'TOMO 2: Rutinas Diarias de Activación',
    subtitle: 'Una estructura para cada momento del día',
    desc: 'Las rutinas son la columna vertebral de una mente sana. Este tomo le da al adulto mayor una actividad concreta para cada momento del día, desde que se levanta hasta que se duerme.',
    bullets: ['Activación matutina para empezar el día con la mente encendida', 'Repaso de la mañana para trabajar la memoria reciente', 'Pausa activa del mediodía para recargar el cerebro', 'Cierre de tarde para consolidar los recuerdos del día', 'Ritual de noche para preparar la mente para el descanso', 'Balance semanal para ver el progreso real']
  }, {
    icon: <Book className="w-8 h-8 text-teal-600" data-id="element-279" />,
    title: 'TOMO 3: Gimnasia Cerebral Progresiva',
    subtitle: 'El entrenamiento técnico que desafía la mente de verdad',
    desc: 'Ejercicios organizados en tres niveles de dificultad creciente. Siempre al borde de lo cómodo, nunca frustrante, nunca aburrido.',
    bullets: ['Ejercicios de fluidez verbal y vocabulario activo', 'Cálculo mental y series numéricas', 'Memoria visual y retención de información', 'Atención sostenida y control de la distracción', 'Razonamiento lógico y resolución de problemas', 'Desafíos cognitivos avanzados para los días de más energía']
  }, {
    icon: <Gamepad2 className="w-8 h-8 text-teal-600" data-id="element-280" />,
    title: 'TOMO 4: Juegos de Estimulación Lúdica',
    subtitle: 'Porque cuando el cerebro se divierte, aprende el doble',
    desc: 'El juego no es solo para los chicos. Para el cerebro adulto, jugar es una de las formas más poderosas de estimulación cognitiva porque activa la motivación y la atención sin que se sienta como una obligación.',
    bullets: ['Juego de las diferencias para entrenar la atención visual', 'Sopa de letras temática con referencias argentinas', 'Crucigrama de la memoria con palabras del mundo cotidiano', 'Juego de personajes para trabajar la memoria biográfica', 'Bingo de la memoria para hacer en familia', 'El gran desafío familiar donde el adulto mayor es el protagonista']
  }, {
    icon: <Lightbulb className="w-8 h-8 text-teal-600" data-id="element-281" />,
    title: 'TOMO 5: Guía Práctica Anti-Olvidos',
    subtitle: 'Soluciones concretas para los olvidos que más molestan en el día a día',
    desc: 'No hay olvido cotidiano que no tenga una técnica específica para reducirlo. Este tomo ataca uno por uno los olvidos más frecuentes con herramientas que se pueden aplicar desde el mismo día.',
    bullets: ['Técnica del lugar fijo para nunca más perder las llaves o los anteojos', 'Sistema de asociación para recordar nombres de personas', 'Agenda anti-olvidos para turnos y compromisos importantes', 'Técnica del ancla de intención para no olvidar para qué fuiste a un lugar', 'Sistema de vaciado mental para reducir la sobrecarga cognitiva', 'Técnicas de memoria profunda usadas por especialistas en rendimiento cognitivo']
  }, {
    icon: <Heart className="w-8 h-8 text-teal-600" data-id="element-282" />,
    title: 'TOMO 6: Plan Conexión Familiar',
    subtitle: 'El tomo que convierte cada encuentro familiar en estimulación cognitiva real',
    desc: 'Este es el tomo más especial del sistema. No solo trabaja la memoria del adulto mayor. Trabaja el vínculo entre generaciones. Cada actividad es una excusa perfecta para que hijos, nietos y abuelos se sienten juntos y construyan algo que no tiene precio.',
    bullets: ['La entrevista de la vida para conocer historias que nunca escucharon', 'El baúl de los recuerdos para revivir momentos significativos', 'El árbol de la familia para reconstruir la historia genealógica', 'La receta de la abuela para preservar un tesoro familiar', 'Cartas que nunca se enviaron para decir lo que el corazón guarda', 'El gran álbum familiar para darle historia a cada foto']
  }];
  return <section className="py-20 px-4 bg-[#EFE4CC]" data-id="element-283">
      <div className="max-w-4xl mx-auto" data-id="element-284">
        <div className="text-center mb-16" data-id="element-285">
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6" data-id="element-286">
            QUÉ TE LLEVÁS HOY
          </h2>
          <div className="bg-white border border-orange-200 rounded-2xl p-8 text-left space-y-4 shadow-md" data-id="element-287">
            <h3 className="text-2xl font-bold text-teal-700" data-id="element-288">
              El Sistema de Activación Progresiva
            </h3>
            <p className="text-slate-700" data-id="element-289">
              El error más común es ir directo a los ejercicios sin preparar el
              terreno.{' '}
              <span className="text-slate-900 font-semibold" data-id="element-290">
                La mente se activa con hábitos correctos en el orden correcto.
              </span>
            </p>
            <p className="text-slate-600 italic" data-id="element-291">
              Cada tomo prepara al siguiente: los hábitos del Tomo 1 hacen
              funcionar los ejercicios del Tomo 3, y el vínculo familiar del
              Tomo 6 multiplica todo lo anterior.
            </p>
            <p className="text-slate-900 font-bold text-lg pt-4" data-id="element-292">
              Un solo sistema. En todas las fases. Para siempre.
            </p>
          </div>
        </div>

        <h3 className="text-3xl font-bold text-center text-slate-900 mb-12" data-id="element-293">
          Los 6 Tomos del Sistema
        </h3>

        <div className="space-y-8" data-id="element-294">
          {tomos.map((tomo, idx) => <div key={idx} className="bg-white border border-orange-200 rounded-2xl p-6 md:p-8 hover:border-teal-500 hover:shadow-lg transition-all shadow-sm" data-id="element-295">
              <div className="flex items-start gap-4 mb-6" data-id="element-296">
                <div className="bg-teal-50 p-3 rounded-xl border border-teal-100" data-id="element-297">
                  {tomo.icon}
                </div>
                <div data-id="element-298">
                  <h4 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-299">
                    📘 {tomo.title}
                  </h4>
                  <p className="text-teal-700 font-medium" data-id="element-300">{tomo.subtitle}</p>
                </div>
              </div>
              <p className="text-slate-700 mb-6 text-lg" data-id="element-301">{tomo.desc}</p>
              <ul className="space-y-3" data-id="element-302">
                {tomo.bullets.map((bullet, bIdx) => <li key={bIdx} className="flex items-start gap-3 text-slate-600" data-id="element-303">
                    <ArrowRight className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" data-id="element-304" />
                    <span data-id="element-305">{bullet}</span>
                  </li>)}
              </ul>
            </div>)}
        </div>

        <div className="mt-16 text-center" data-id="element-306">
          <CTAButton text="QUIERO EL SISTEMA COMPLETO →" data-id="element-307" />
        </div>
      </div>
    </section>;
}